import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel,
  FormGroup, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Stack, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Add, DeleteOutline, PlayArrow } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Catalogue, Indicator, IndicatorDefinition, IndicatorUnit, MeasureRef, ResultRow, Threshold,
  errorMessage, indicatorRegistryApi,
} from '../../../services/indicatorRegistryApi';
import FormulaBuilder from './FormulaBuilder';
import MeasurePicker from './MeasurePicker';
import ResultsTable, { formatValue } from './ResultsTable';

const LAST_YEAR = new Date().getFullYear() - 1;
const YEARS = Array.from({ length: 6 }, (_, i) => LAST_YEAR + 1 - i);

const DEFAULT_MULTIPLIER: Record<IndicatorUnit, number> = { count: 1, ratio: 1, index: 1, percent: 100 };

function blankDefinition(catalogue: Catalogue): IndicatorDefinition {
  const first = catalogue.measures[0]?.code || '';
  return {
    code: '', name: '', short_name: '', category: 'other', definition: '', rationale: '',
    interpretation: '', limitations: '', sdg_code: '',
    unit: 'ratio', multiplier: 1, decimals: 1,
    numerator: { measure: first, filters: {} },
    denominator: { measure: first, filters: {} },
    terms: {}, formula: '',
    match_schools: true, breakdowns: [], thresholds: [],
  };
}

/** Dimensions every side or part can be broken down by, and that aren't already filters. */
function breakdownOptions(def: IndicatorDefinition, catalogue: Catalogue): string[] {
  const sides = (def.formula !== '' || Object.keys(def.terms).length
    ? Object.values(def.terms)
    : [def.numerator, def.denominator].filter(Boolean)) as MeasureRef[];
  if (!sides.length) return [];
  return catalogue.dimensions
    .map(d => d.code)
    .filter(code => sides.every(side => {
      const m = catalogue.measures.find(x => x.code === side.measure);
      return m?.dimensions.some(d => d.code === code) && !(code in side.filters);
    }));
}

interface Props {
  catalogue: Catalogue;
  initial?: Indicator;
  onDone: (indicator?: Indicator) => void;
}

const IndicatorDesigner: React.FC<Props> = ({ catalogue, initial, onDone }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [def, setDef] = useState<IndicatorDefinition>(() => (
    initial ? { ...initial, terms: initial.terms || {}, formula: initial.formula || '' } : blankDefinition(catalogue)
  ));
  const [mode, setMode] = useState<'simple' | 'formula'>(initial?.formula ? 'formula' : 'simple');
  const isFormula = mode === 'formula';
  const [preview, setPreview] = useState({ year: LAST_YEAR, geo_level: 'district', breakdown: '' });

  const set = <K extends keyof IndicatorDefinition>(key: K, value: IndicatorDefinition[K]) =>
    setDef(d => ({ ...d, [key]: value }));

  const allowedBreakdowns = useMemo(() => breakdownOptions(def, catalogue), [def, catalogue]);
  const breakdowns = def.breakdowns.filter(b => allowedBreakdowns.includes(b));
  const dimLabel = (code: string) => catalogue.dimensions.find(d => d.code === code)?.label || code;

  const changeUnit = (unit: IndicatorUnit) => setDef(d => (isFormula ? { ...d, unit } : {
    ...d,
    unit,
    multiplier: DEFAULT_MULTIPLIER[unit],
    denominator: unit === 'count' ? null : d.denominator || { measure: d.numerator.measure, filters: {} },
  }));

  /** Move between the two ways of calculating, carrying over what was already picked. */
  const changeMode = (next: 'simple' | 'formula' | null) => {
    if (!next || next === mode) return;
    setMode(next);
    setDef(d => {
      if (next === 'formula') {
        const terms: Record<string, MeasureRef> = { A: { ...d.numerator, label: '' } };
        if (d.denominator) terms.B = { ...d.denominator, label: '' };
        const base = d.denominator ? 'A ÷ B' : 'A';
        // In a formula the × 100 is written into the formula itself.
        return { ...d, terms, formula: d.multiplier === 100 ? `${base} × 100` : base, multiplier: 1 };
      }
      const first = catalogue.measures[0]?.code || '';
      const strip = (p?: MeasureRef): MeasureRef | undefined => (p ? { measure: p.measure, filters: p.filters, ...(p.year_offset ? { year_offset: p.year_offset } : {}) } : undefined);
      const numerator = strip(d.terms.A) || { measure: first, filters: {} };
      const denominator = d.unit === 'count' ? null : strip(d.terms.B) || { measure: numerator.measure, filters: {} };
      return { ...d, numerator, denominator, terms: {}, formula: '', multiplier: DEFAULT_MULTIPLIER[d.unit] };
    });
  };

  const setThreshold = (i: number, patch: Partial<Threshold>) =>
    set('thresholds', def.thresholds.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  const payload = (): IndicatorDefinition => (isFormula
    ? { ...def, breakdowns, multiplier: 1 }
    : { ...def, breakdowns, terms: {}, formula: '' });

  const previewMutation = useMutation({
    mutationFn: (params: typeof preview) => indicatorRegistryApi.preview({
      numerator: def.numerator, denominator: def.denominator,
      terms: isFormula ? def.terms : {}, formula: isFormula ? def.formula : '',
      unit: def.unit,
      multiplier: def.multiplier, decimals: def.decimals, match_schools: def.match_schools,
      thresholds: def.thresholds, ...params,
    }),
  });

  const saveMutation = useMutation({
    mutationFn: async (submit: boolean) => {
      const saved = initial
        ? await indicatorRegistryApi.update(initial.id, payload())
        : await indicatorRegistryApi.create(payload());
      return submit ? indicatorRegistryApi.transition(saved.id, 'submit') : saved;
    },
    onSuccess: (saved, submit) => {
      queryClient.invalidateQueries(['indicator-registry']);
      enqueueSnackbar(submit ? `${saved.code} sent for review` : `${saved.code} saved as draft`, { variant: 'success' });
      onDone(saved);
    },
  });

  // Show what the last test ran with, even if the selectors have changed since.
  const result = previewMutation.data;
  const ran = previewMutation.variables || preview;
  const national = result?.rows.find(r => r.geo_level === 'national' && !r.breakdown);
  const tableRows: ResultRow[] = (result?.rows || []).filter(r =>
    r.geo_level === ran.geo_level && r.breakdown === ran.breakdown);

  return (
    <Grid container spacing={3}>
      {/* Definition */}
      <Grid item xs={12} lg={7}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {initial ? `Edit ${initial.code} (version ${initial.version})` : 'New indicator'}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Code" size="small" fullWidth required value={def.code} disabled={!!initial}
                helperText="e.g. MOE050" onChange={e => set('code', e.target.value.toUpperCase())}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label="Name" size="small" fullWidth required value={def.name} onChange={e => set('name', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Short name" size="small" fullWidth value={def.short_name} onChange={e => set('short_name', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select label="Category" value={def.category} onChange={e => set('category', e.target.value)}>
                  {catalogue.categories.map(c => <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="SDG 4 code" size="small" fullWidth value={def.sdg_code} placeholder="e.g. 4.c.4" onChange={e => set('sdg_code', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Definition" size="small" fullWidth required multiline minRows={2} value={def.definition} onChange={e => set('definition', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Rationale" size="small" fullWidth multiline minRows={2} value={def.rationale} onChange={e => set('rationale', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="How to read it" size="small" fullWidth multiline minRows={2} value={def.interpretation} onChange={e => set('interpretation', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Limitations" size="small" fullWidth multiline minRows={1} value={def.limitations} onChange={e => set('limitations', e.target.value)} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Calculation</Typography>
          <ToggleButtonGroup exclusive size="small" color="primary" value={mode} onChange={(_, v) => changeMode(v)} sx={{ mb: 1 }}>
            <ToggleButton value="simple">Simple: a count, or A ÷ B</ToggleButton>
            <ToggleButton value="formula">Formula: several parts</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {isFormula
              ? 'Combine up to eight figures with + − × ÷ and brackets, e.g. (A + B) ÷ C × 100. For a percentage, end the formula with × 100.'
              : 'One figure, or one figure divided by another. Switch to Formula to combine more.'}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Unit</InputLabel>
                <Select label="Unit" value={def.unit} onChange={e => changeUnit(e.target.value as IndicatorUnit)}>
                  {catalogue.units.map(u => <MenuItem key={u.code} value={u.code}>{u.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {!isFormula && <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Multiply by</InputLabel>
                <Select label="Multiply by" value={def.multiplier} onChange={e => set('multiplier', Number(e.target.value))}>
                  {[1, 100, 1000].map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>}
            <Grid item xs={6} sm={4}>
              <TextField
                label="Decimals" size="small" type="number" fullWidth value={def.decimals}
                inputProps={{ min: 0, max: 4 }} onChange={e => set('decimals', Number(e.target.value))}
              />
            </Grid>
          </Grid>

          {isFormula ? (
            <>
              <FormulaBuilder
                catalogue={catalogue}
                terms={def.terms}
                formula={def.formula}
                onChange={(terms, formula) => setDef(d => ({ ...d, terms, formula }))}
              />
              {Object.keys(def.terms).length > 1 && (
                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={<Switch checked={def.match_schools} onChange={e => set('match_schools', e.target.checked)} />}
                  label={
                    <Typography variant="body2">
                      Only count schools that report every part
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Keep on when parts come from different returns (e.g. students and classrooms).
                        Turn off when all parts are the same kind of record, such as girls and boys enrolled.
                      </Typography>
                    </Typography>
                  }
                />
              )}
            </>
          ) : (
          <Stack spacing={2}>
            <MeasurePicker label={def.denominator ? 'Numerator' : 'Count'} value={def.numerator} catalogue={catalogue} onChange={v => set('numerator', v)} />
            {def.denominator && (
              <>
                <Typography variant="body2" align="center" color="text.secondary">divided by</Typography>
                <MeasurePicker label="Denominator" value={def.denominator} catalogue={catalogue} onChange={v => set('denominator', v)} />
                <FormControlLabel
                  control={<Switch checked={def.match_schools} onChange={e => set('match_schools', e.target.checked)} />}
                  label={
                    <Typography variant="body2">
                      Only count schools that report both
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Keep on when the two sides come from different returns (e.g. students and classrooms).
                        Turn off when both sides use the same measure, such as girls ÷ boys.
                      </Typography>
                    </Typography>
                  }
                />
              </>
            )}
          </Stack>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Breakdowns</Typography>
          <Typography variant="caption" color="text.secondary">
            Values are always published by region, district and school. Pick extra breakdowns to publish.
          </Typography>
          <FormGroup row sx={{ mt: 1 }}>
            {catalogue.dimensions.map(d => (
              <FormControlLabel
                key={d.code}
                disabled={!allowedBreakdowns.includes(d.code)}
                control={
                  <Checkbox
                    checked={breakdowns.includes(d.code)}
                    onChange={e => set('breakdowns', e.target.checked
                      ? [...breakdowns, d.code]
                      : breakdowns.filter(b => b !== d.code))}
                  />
                }
                label={d.label}
              />
            ))}
          </FormGroup>

          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Thresholds</Typography>
              <Typography variant="caption" color="text.secondary">The first band a value falls in sets its status.</Typography>
            </Box>
            <Button size="small" startIcon={<Add />} onClick={() => set('thresholds', [...def.thresholds, { status: 'good', min: null, max: null }])}>
              Add band
            </Button>
          </Box>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {def.thresholds.map((t, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Select size="small" value={t.status} sx={{ width: 120 }} onChange={e => setThreshold(i, { status: e.target.value as Threshold['status'] })}>
                  <MenuItem value="good">Good</MenuItem>
                  <MenuItem value="watch">Watch</MenuItem>
                  <MenuItem value="poor">Poor</MenuItem>
                </Select>
                <TextField
                  size="small" label="From" type="number" value={t.min ?? ''} sx={{ width: 120 }}
                  onChange={e => setThreshold(i, { min: e.target.value === '' ? null : Number(e.target.value) })}
                />
                <TextField
                  size="small" label="To" type="number" value={t.max ?? ''} sx={{ width: 120 }}
                  onChange={e => setThreshold(i, { max: e.target.value === '' ? null : Number(e.target.value) })}
                />
                <IconButton size="small" aria-label="Remove band" onClick={() => set('thresholds', def.thresholds.filter((_, j) => j !== i))}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>

          {saveMutation.isError && <Alert severity="error" sx={{ mt: 3 }}>{errorMessage(saveMutation.error)}</Alert>}
          {initial?.review_note && (
            <Alert severity="info" sx={{ mt: 3 }}>Reviewer's note: {initial.review_note}</Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={() => onDone()}>Cancel</Button>
            <Button variant="outlined" disabled={saveMutation.isLoading} onClick={() => saveMutation.mutate(false)}>
              Save draft
            </Button>
            <Button variant="contained" disabled={saveMutation.isLoading} onClick={() => saveMutation.mutate(true)}>
              Save and send for review
            </Button>
          </Box>
        </Paper>
      </Grid>

      {/* Preview */}
      <Grid item xs={12} lg={5}>
        <Paper variant="outlined" sx={{ p: 3, position: { lg: 'sticky' }, top: { lg: 16 } }}>
          <Typography variant="h6">Test on real data</Typography>
          <Typography variant="caption" color="text.secondary">
            Runs the same calculation that publishing will run. Nothing is saved.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Year</InputLabel>
              <Select label="Year" value={preview.year} onChange={e => setPreview(p => ({ ...p, year: Number(e.target.value) }))}>
                {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Level</InputLabel>
              <Select label="Level" value={preview.geo_level} onChange={e => setPreview(p => ({ ...p, geo_level: e.target.value }))}>
                {catalogue.geo_levels.map(g => <MenuItem key={g.code} value={g.code}>{g.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel shrink>Breakdown</InputLabel>
              <Select label="Breakdown" displayEmpty notched value={preview.breakdown} onChange={e => setPreview(p => ({ ...p, breakdown: e.target.value }))}>
                <MenuItem value="">None</MenuItem>
                {allowedBreakdowns.map(b => <MenuItem key={b} value={b}>{dimLabel(b)}</MenuItem>)}
              </Select>
            </FormControl>
            <Button
              variant="contained" startIcon={previewMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
              disabled={previewMutation.isLoading} onClick={() => previewMutation.mutate(preview)}
            >
              Test
            </Button>
          </Stack>

          {previewMutation.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(previewMutation.error)}</Alert>}

          {result && (
            <Box sx={{ mt: 2 }}>
              {result.warnings.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
              {national && (
                <Box sx={{ my: 2 }}>
                  <Typography variant="caption" color="text.secondary">Zanzibar, {ran.year}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(national.value, def.unit, def.decimals)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    from {national.schools.toLocaleString()} schools
                  </Typography>
                </Box>
              )}
              <ResultsTable
                rows={tableRows}
                unit={def.unit}
                decimals={def.decimals}
                hasDenominator={isFormula ? tableRows.some(r => r.denominator !== null) : !!def.denominator}
                breakdownLabel={ran.breakdown ? dimLabel(ran.breakdown) : undefined}
              />
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default IndicatorDesigner;
